/**
 * HowToPage — the `/how-to` Help hub (CLARID-UX-2026-06, PR 4).
 *
 * A browsable, searchable reference auto-generated from the SAME shared source
 * the inline intros read (`helpContent` + `glossary`). Because it reads the
 * source rather than restating it, the hub can never drift from what users see
 * inline. Per-module reference cards (grouped by section) + a searchable
 * glossary. Aimed at training partners and new users who want the whole map in
 * one place.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, QuestionMarkCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { helpContent, glossary, moduleKeys, glossaryTermKeys } from '../lib/help';
import type { ModuleKey } from '../lib/help';
import { moduleRoutes } from '../lib/help/moduleRoutes';
import ModuleConnections from '../components/help/ModuleConnections';
import GlossaryTooltip from '../components/help/GlossaryTooltip';
import AdvisoryFirmGuide from '../components/help/AdvisoryFirmGuide';

/** Section grouping for the hub, mirroring the app's nav structure. Any module
 * not listed here still appears under "More" (see `sections` below), so the hub
 * stays complete even if this drifts. */
const HUB_SECTIONS: { label: string; modules: ModuleKey[] }[] = [
  {
    label: 'Marketing',
    modules: [
      'marketing-upload',
      'marketing-reports',
      'content-library',
      'pending-reviews',
      'website-scan',
      'social-media',
      'mcp-checks',
      'marketing-analytics',
    ],
  },
  {
    label: 'HMDA',
    modules: [
      'hmda-lar-builder',
      'hmda-field-guide',
      'hmda-lar-validation',
      'hmda-edit-intelligence',
      'hmda-geocoding',
      'hmda-apor',
      'hmda-cfpb-filing',
      'hmda-quality',
    ],
  },
  {
    label: 'Fair Lending',
    modules: [
      'fair-lending',
      'alert-upload',
      'fair-lending-risk-assessments',
      'fair-lending-loan-officers',
      'fair-lending-redlining',
      'fair-lending-redlining-map',
      'fair-lending-regression',
      'fair-lending-content-findings',
      'exceptions',
    ],
  },
  {
    label: 'CRA',
    modules: [
      'cra',
      'cra-assessment-areas',
      'cra-sb-loans',
      'cra-lending-analysis',
      'cra-lending-test',
      'cra-cd-activities',
      'cra-gap-analysis',
      'cra-public-file',
      'crad-report',
      'cra-large-bank',
    ],
  },
  {
    label: 'Exam & Regulatory',
    modules: [
      'exam-readiness',
      'exams',
      'compliance-hub',
      'regulatory-intelligence',
      'enforcement-intelligence',
      'state-compliance',
    ],
  },
  {
    label: 'Program & Platform',
    modules: [
      'calendar',
      'board-reports',
      'peer-benchmarking',
      'policies',
      'procedures',
      'complaints',
      'vendor-compliance',
      'training',
      'regulations',
      'product-profiles',
      'custom-rules',
      'ai-performance',
      'dashboard',
      'branches',
      'organization',
    ],
  },
];

function ModuleCard({ moduleKey }: { moduleKey: ModuleKey }) {
  const entry = helpContent[moduleKey];
  const route = moduleRoutes[moduleKey];
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">{route.label}</h3>
        <Link
          to={route.path}
          className="whitespace-nowrap text-xs font-medium text-teal-700 hover:text-teal-800 hover:underline"
        >
          Open →
        </Link>
      </div>
      <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{entry.summary}</p>
      {entry.points.length > 0 && (
        <ul className="mt-2 space-y-1">
          {entry.points.map((point, i) => (
            <li key={i} className="flex gap-2 text-xs text-gray-600">
              <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-teal-500" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-gray-500">
        <span className="font-medium text-gray-700">Start by:</span> {entry.firstAction}
        {entry.runtime && <span className="text-gray-400"> · runs in {entry.runtime}</span>}
      </p>
      <ModuleConnections moduleKey={moduleKey} className="mt-3" />
      {entry.glossary.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-xs font-medium text-gray-500">Key terms:</span>
          {entry.glossary.map((term) => (
            <GlossaryTooltip key={term} termKey={term} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function HowToPage() {
  const [query, setQuery] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  const q = query.trim().toLowerCase();

  // Sections, with any uncategorized module appended under "More" so nothing
  // is ever silently dropped from the hub.
  const sections = useMemo(() => {
    const categorized = new Set(HUB_SECTIONS.flatMap((s) => s.modules));
    const leftover = moduleKeys.filter((k) => !categorized.has(k));
    return leftover.length ? [...HUB_SECTIONS, { label: 'More', modules: leftover }] : HUB_SECTIONS;
  }, []);

  const moduleMatches = (key: ModuleKey) => {
    if (!q) return true;
    const e = helpContent[key];
    return [moduleRoutes[key].label, e.title, e.summary, ...e.points]
      .join(' ')
      .toLowerCase()
      .includes(q);
  };

  const visibleGlossary = glossaryTermKeys.filter((key) => {
    if (!q) return true;
    const g = glossary[key];
    return `${g.term} ${g.plainDefinition}`.toLowerCase().includes(q);
  });

  const visibleSections = sections
    .map((s) => ({ ...s, modules: s.modules.filter(moduleMatches) }))
    .filter((s) => s.modules.length > 0);

  const noResults = q !== '' && visibleSections.length === 0 && visibleGlossary.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <QuestionMarkCircleIcon className="h-8 w-8 text-teal-600" />
          <h1 className="text-2xl font-bold text-gray-900">How-To &amp; Glossary</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Every module explained in one place, plus a glossary of the terms you’ll see across Clarid.
          The same explanations appear inline on each page.
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search modules and terms…"
          className="block w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
      </div>

      {noResults && (
        <p className="text-sm text-gray-500">No modules or terms match “{query}”.</p>
      )}

      {/* Getting started for advisory firms / channel partners — the multi-bank
          engagement path the bank onboarding wizards don't cover. Hidden during
          search so it never competes with results. Collapsed by default. */}
      {q === '' && (
        <section className="rounded-lg border border-teal-100 bg-teal-50/40">
          <button
            type="button"
            onClick={() => setGuideOpen((o) => !o)}
            aria-expanded={guideOpen}
            className="flex w-full items-center justify-between gap-3 p-5 text-left"
          >
            <span>
              <span className="block text-sm font-semibold text-gray-900">Getting started as an advisory firm</span>
              <span className="mt-0.5 block text-xs text-gray-500">
                Running Clarid across multiple client banks — the engagement lifecycle, start to finish.
              </span>
            </span>
            <ChevronDownIcon
              className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${guideOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {guideOpen && <AdvisoryFirmGuide className="px-5 pb-6" />}
        </section>
      )}

      {/* Module reference cards by section */}
      {visibleSections.map((section) => (
        <section key={section.label}>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-400">
            {section.label}
          </h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {section.modules.map((key) => (
              <ModuleCard key={key} moduleKey={key} />
            ))}
          </div>
        </section>
      ))}

      {/* Glossary */}
      {visibleGlossary.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-400">
            Glossary
          </h2>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 rounded-lg border border-gray-200 bg-white p-5 sm:grid-cols-2">
            {visibleGlossary.map((key) => {
              const g = glossary[key];
              return (
                <div key={key}>
                  <dt className="text-sm font-semibold text-gray-900">{g.term}</dt>
                  <dd className="mt-0.5 text-sm leading-relaxed text-gray-600">
                    {g.plainDefinition}
                    {g.regCite && <span className="mt-0.5 block text-xs text-gray-400">{g.regCite}</span>}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>
      )}
    </div>
  );
}
